<template>
  <div class="waiver-page">
    <!-- Estado terminal: token inválido / vencido -->
    <div
      v-if="state === 'invalid'"
      class="waiver-card waiver-center"
    >
      <v-icon
        size="48"
        color="warning"
      >
        {{ icons.mdiQrcodeRemove }}
      </v-icon>
      <h1 class="waiver-h1 mt-4">
        {{ t('waiver.invalid_title') }}
      </h1>
      <p class="waiver-sub">
        {{ t('waiver.invalid_body') }}
      </p>
    </div>

    <!-- Cargando -->
    <div
      v-else-if="state === 'loading'"
      class="waiver-card waiver-center"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <!-- Firmado -->
    <div
      v-else-if="state === 'done'"
      class="waiver-card waiver-center"
    >
      <v-icon
        size="56"
        color="success"
      >
        {{ icons.mdiCheckCircle }}
      </v-icon>
      <h1 class="waiver-h1 mt-4">
        {{ t('waiver.done_title', { club: page.club.name }) }}
      </h1>
      <p class="waiver-sub">
        {{ t('waiver.done_body') }}
      </p>
      <v-btn
        depressed
        large
        class="mt-4 waiver-cta"
        @click="downloadApp"
      >
        {{ t('waiver.get_app') }}
      </v-btn>
    </div>

    <template v-else>
      <!-- Eyebrow del club + reserva -->
      <div class="waiver-eyebrow">
        <img
          v-if="page.club.logo"
          :src="page.club.logo"
          alt=""
          class="waiver-logo"
        >
        <span>{{ page.club.name }} · {{ reservationLabel }}</span>
      </div>

      <!-- Paso 1 · ¿Quién sos? -->
      <div
        v-if="step === 1"
        class="waiver-card"
      >
        <h1 class="waiver-h1">
          {{ t('waiver.who_title') }}
        </h1>
        <p class="waiver-sub">
          {{ t('waiver.who_sub') }}
        </p>

        <!-- Roster como tarjetas (mockup): iniciales, nombre, radio a la
             derecha. Los que ya firmaron van apagados con el tilde verde.
             Botones con role=radio para que el teclado y el lector los
             traten como el grupo que son. -->
        <div
          class="waiver-roster"
          role="radiogroup"
        >
          <button
            v-for="p in page.players"
            :key="p.player_match_id"
            type="button"
            role="radio"
            class="waiver-player"
            :class="{ 'is-on': pickedPlayer === p.player_match_id, 'is-signed': p.signed }"
            :aria-checked="pickedPlayer === p.player_match_id ? 'true' : 'false'"
            :disabled="p.signed"
            @click="pickPlayer(p)"
          >
            <span class="waiver-avatar">
              <v-icon
                v-if="!p.display_name"
                size="16"
                class="waiver-avatar-icon"
              >
                {{ icons.mdiAccount }}
              </v-icon>
              <template v-else>{{ initials(p.display_name) }}</template>
            </span>
            <span class="waiver-player-name">
              {{ p.display_name || t('waiver.guest') }}
              <span
                v-if="!p.display_name"
                class="waiver-player-meta"
              >· {{ t('waiver.guest_slot', { n: p.slot }) }}</span>
              <span
                v-if="p.signed"
                class="waiver-player-meta"
              >· {{ t('waiver.already_signed') }}</span>
            </span>
            <span
              v-if="p.signed"
              class="waiver-check"
            >
              <v-icon size="14">{{ icons.mdiCheck }}</v-icon>
            </span>
            <span
              v-else
              class="waiver-radio"
            />
          </button>
        </div>

        <div class="waiver-divider">
          {{ t('waiver.not_on_list') }}
        </div>
        <v-text-field
            dark
          v-model="typedName"
          :placeholder="t('waiver.type_full_name')"
          outlined
          dense
          hide-details
          @focus="pickedPlayer = null"
        />

        <v-btn
          depressed
          large
          block
          class="mt-5 waiver-cta"
          :disabled="!canContinue"
          @click="goToForm"
        >
          {{ t('waiver.continue') }}
        </v-btn>
      </div>

      <!-- Paso 2 · Formulario + documento + firma -->
      <div
        v-else
        class="waiver-card"
      >
        <button
          type="button"
          class="waiver-back"
          @click="step = 1"
        >
          ‹ {{ t('waiver.back') }}
        </button>
        <h1 class="waiver-h1">
          {{ t('waiver.title') }}
        </h1>

        <v-form
          ref="formRef"
          class="waiver-grid"
          @submit.prevent="submit"
        >
          <div class="waiver-col waiver-col--form">
          <label class="waiver-label">{{ t('waiver.full_name') }}</label>
          <v-text-field
            dark
            v-model="form.fullName"
            outlined
            dense
            :rules="[rules.required, rules.twoWords]"
            autocomplete="name"
          />

          <label class="waiver-label">{{ t('waiver.phone') }}</label>
          <div class="d-flex">
            <v-autocomplete
            dark
              v-model="form.phoneCode"
              class="pr-1 waiver-code"
              dense
              outlined
              :items="phoneCodeOptions"
              item-text="text"
              item-value="value"
              :rules="[rules.required]"
            >
              <template #item="{ item }">
                <img
                  :src="item.image"
                  :alt="item.value"
                  width="25"
                  height="auto"
                  class="mr-2"
                >
                {{ item.text }} {{ item.name }}
              </template>
            </v-autocomplete>
            <v-text-field
            dark
              v-model="form.phone"
              class="pl-1"
              dense
              outlined
              type="tel"
              inputmode="tel"
              autocomplete="tel-national"
              :rules="[rules.required, rules.phone]"
            />
          </div>

          <label class="waiver-label">{{ t('waiver.email') }}</label>
          <v-text-field
            dark
            v-model="form.email"
            outlined
            dense
            type="email"
            inputmode="email"
            autocomplete="email"
            :rules="[rules.required, rules.email]"
          />

          <div class="waiver-consent-box">
            <v-checkbox
              v-model="form.marketing"
              dark
              class="mt-0 pt-0"
              hide-details
              :label="t('waiver.marketing', { club: page.club.name })"
            />
          </div>

          </div>

          <!-- Documento completo, con toggle de idioma si el club cargó ES.
               En tablet va en su propia columna, a la izquierda del
               formulario y del pad (el jugador lee y firma en la misma
               pantalla, sin scroll). -->
          <div class="waiver-col waiver-col--doc">
          <div class="waiver-doc-head">
            <span class="waiver-eyebrow-text">
              {{ page.club.name }} · v{{ page.waiver.version }} · {{ publishedLabel }}
            </span>
            <div
              v-if="page.waiver.es"
              class="waiver-lang"
            >
              <button
                type="button"
                :class="{ on: docLang === 'en' }"
                @click="docLang = 'en'"
              >
                EN
              </button>
              <button
                type="button"
                :class="{ on: docLang === 'es' }"
                @click="docLang = 'es'"
              >
                ES
              </button>
            </div>
          </div>
          <h2 class="waiver-doc-title">
            {{ page.waiver.title }}
          </h2>
          <div class="waiver-doc">
            <p
              v-for="(p, i) in paragraphs"
              :key="i"
              :class="{ 'waiver-conspicuous': p.conspicuous }"
            >
              {{ p.text }}
            </p>
          </div>
          </div>

          <div class="waiver-col waiver-col--sign">
          <!-- Pad de firma -->
          <label class="waiver-label">{{ t('waiver.sign_label') }}</label>
          <div class="waiver-pad">
            <canvas
              ref="pad"
              class="waiver-canvas"
              @pointerdown="padStart"
              @pointermove="padMove"
              @pointerup="padEnd"
              @pointercancel="padEnd"
              @pointerleave="padEnd"
            />
            <button
              type="button"
              class="waiver-clear"
              @click="padClear"
            >
              {{ t('waiver.clear') }}
            </button>
          </div>
          <p class="waiver-econsent">
            {{ doc.econsent_label }}
          </p>
          <p
            v-if="errorMsg"
            class="waiver-error"
          >
            {{ errorMsg }}
          </p>

          <v-btn
            depressed
            large
            block
            class="waiver-cta"
            type="submit"
            :loading="submitting"
            :disabled="!hasStroke || submitting"
          >
            {{ t('waiver.sign_cta') }}
          </v-btn>
          </div>
        </v-form>
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable import/no-cycle */
import { ref, computed, onMounted } from '@vue/composition-api'
import { mdiAccount, mdiCheck, mdiCheckCircle, mdiQrcodeRemove } from '@mdi/js'
import { useRouter, defaultDownloadAppByOS } from '@core/utils'
import usePhone from '@core/utils/usePhone'
import { getPublicWaiver, signPublicWaiver } from '@api'
import { i18n, getI18nLanguage } from '@/plugins/i18n'

/**
 * Waiver web por QR (ticket 10, mockup sección 2). Un token por reserva: el
 * jugador elige quién es en el paso 1 (o tipea su nombre si no está en la
 * lista), y en el paso 2 completa nombre, teléfono, email, lee el documento
 * y firma dibujando. Sin OTP: la atribución la dan la cadena de custodia
 * del link (lo emitió un operador para ESTA reserva), los datos tipeados y
 * la firma. Query: `?pm=<player_match_id>` preselecciona (viene del Manager),
 * `?op=<token>` marca la firma como tomada en el mostrador.
 */
export default {
  name: 'WaiverSign',
  setup() {
    // `i18n.t` directo y no el `t` de useUtils: este interpola `{club}` y no
    // usa getCurrentInstance, asi que las reglas de Vuetify (que validan en
    // beforeMount, fuera de setup) pueden llamarlo sin explotar.
    const t = (key, params) => i18n.t(key, params)
    const { route } = useRouter()
    const { phoneCodeOptions } = usePhone()

    const state = ref('loading') // loading | invalid | form | done
    const page = ref(null)
    const step = ref(1)
    const pickedPlayer = ref(null)
    const typedName = ref('')
    const docLang = ref(getI18nLanguage() === 'es' ? 'es' : 'en')
    const form = ref({
      fullName: '',
      phoneCode: null,
      phone: '',
      email: '',
      marketing: false, // nunca pretildado
    })
    const submitting = ref(false)
    const errorMsg = ref('')
    const pad = ref(null)
    const hasStroke = ref(false)
    const formRef = ref(null)

    const token = computed(() => route.value.params.token)
    const opToken = computed(() => route.value.query.op || null)

    const rules = {
      required: v => !!(v && String(v).trim()) || t('waiver.required'),
      twoWords: v => (String(v || '').trim().split(/\s+/).length >= 2) || t('waiver.two_words'),
      phone: v => /^[\d\s().-]{6,}$/.test(String(v || '')) || t('waiver.invalid_phone'),
      email: v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '')) || t('waiver.invalid_email'),
    }

    const load = async () => {
      let res = null
      try {
        res = await getPublicWaiver(token.value)
      } catch (e) {
        // axiosApi.buildErrorMessage revienta si no hay `response` (red
        // caída, mixed content): sin datos no hay página, se muestra el
        // estado de código inválido en vez de un spinner eterno.
        res = null
      }
      if (!res || res.ok !== 1 || !res.data) {
        state.value = 'invalid'

        return
      }
      page.value = res.data
      if (!res.data.waiver.es) docLang.value = 'en'
      // ?pm= del Manager: el operador ya eligió al jugador.
      const pm = Number(route.value.query.pm)
      const pre = pm && res.data.players.find(p => p.player_match_id === pm && !p.signed)
      if (pre) {
        pickedPlayer.value = pre.player_match_id
        form.value.fullName = pre.is_guest ? '' : (pre.display_name || '')
        step.value = 2
      }
      state.value = 'form'
    }

    const canContinue = computed(() => !!pickedPlayer.value || typedName.value.trim().split(/\s+/).length >= 2)

    // Elegir una fila descarta lo tipeado en "no estoy en la lista", y al
    // revés (el @focus del input): una sola identidad a la vez.
    const pickPlayer = p => {
      if (p.signed) return
      pickedPlayer.value = pickedPlayer.value === p.player_match_id ? null : p.player_match_id
      if (pickedPlayer.value) typedName.value = ''
    }
    const initials = name => (name || '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(w => w[0])
      .join('')
      .toUpperCase()

    const goToForm = () => {
      if (pickedPlayer.value) {
        const p = page.value.players.find(x => x.player_match_id === pickedPlayer.value)
        if (p && !p.is_guest && p.display_name && !form.value.fullName) form.value.fullName = p.display_name
      } else if (typedName.value.trim()) {
        form.value.fullName = typedName.value.trim()
      }
      step.value = 2
    }

    const doc = computed(() => {
      if (!page.value) return {}
      const w = page.value.waiver

      return (docLang.value === 'es' && w.es) ? w.es : w.en
    })

    const paragraphs = computed(() => (doc.value.body || '')
      .split(/\n\s*\n/)
      .map(x => x.trim())
      .filter(Boolean)
      .map(text => ({
        text: doc.value.conspicuous && /negligence/i.test(text) ? text.toUpperCase() : text,
        conspicuous: !!doc.value.conspicuous && /negligence/i.test(text),
      })))

    const reservationLabel = computed(() => {
      if (!page.value) return ''
      const r = page.value.reservation

      return [r.date, r.start_time, r.court].filter(Boolean).join(' · ')
    })
    const publishedLabel = computed(() => (page.value && page.value.waiver.published_at
      ? new Date(page.value.waiver.published_at).toLocaleDateString(docLang.value === 'es' ? 'es-AR' : 'en-US')
      : ''))

    // ── Pad de firma: canvas plano, sin librería. Guarda el trazo como PNG. ──
    let ctx = null
    let drawing = false
    const setupPad = () => {
      const c = pad.value
      if (!c) return
      const ratio = window.devicePixelRatio || 1
      const w = c.clientWidth
      const h = c.clientHeight
      c.width = w * ratio
      c.height = h * ratio
      ctx = c.getContext('2d')
      ctx.scale(ratio, ratio)
      ctx.lineWidth = 2.2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = '#111'
    }
    const pos = e => {
      const r = pad.value.getBoundingClientRect()

      return { x: e.clientX - r.left, y: e.clientY - r.top }
    }
    const padStart = e => {
      if (!ctx) setupPad()
      drawing = true
      pad.value.setPointerCapture(e.pointerId)
      const p = pos(e)
      ctx.beginPath()
      ctx.moveTo(p.x, p.y)
    }
    const padMove = e => {
      if (!drawing) return
      const p = pos(e)
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
      hasStroke.value = true
    }
    const padEnd = () => { drawing = false }
    const padClear = () => {
      if (!ctx) return
      ctx.clearRect(0, 0, pad.value.width, pad.value.height)
      hasStroke.value = false
    }
    const padBlob = () => new Promise(resolve => pad.value.toBlob(resolve, 'image/png'))

    const submit = async () => {
      errorMsg.value = ''
      if (!formRef.value.validate() || !hasStroke.value) return
      submitting.value = true
      try {
        const code = phoneCodeOptions.value.find(x => x.value === form.value.phoneCode)
        const digits = String(form.value.phone).replace(/\D/g, '')
        const fd = new FormData()
        fd.append('full_name', form.value.fullName.trim())
        fd.append('phone_code', form.value.phoneCode)
        fd.append('phone_number', `+${code ? code.number : ''}${digits}`)
        fd.append('email', form.value.email.trim())
        fd.append('marketing_consent', form.value.marketing ? 'true' : 'false')
        fd.append('lang', docLang.value)
        if (pickedPlayer.value) fd.append('player_match_id', String(pickedPlayer.value))
        if (opToken.value) fd.append('op', opToken.value)
        fd.append('signature_png', await padBlob(), 'signature.png')
        const res = await signPublicWaiver(token.value, fd)
        if (res && res.ok === 1) {
          state.value = 'done'
        } else {
          errorMsg.value = (res && res.message) || t('waiver.error_generic')
        }
      } finally {
        submitting.value = false
      }
    }

    const downloadApp = () => defaultDownloadAppByOS()

    onMounted(async () => {
      // Default del selector: el país del club (el jugador está parado ahí).
      await load()
      if (page.value && !form.value.phoneCode) {
        const cc = (page.value.club.country_code || 'US').toUpperCase()
        if (phoneCodeOptions.value.some(x => x.value === cc)) form.value.phoneCode = cc
      }
      // noindex: es una página atada a una reserva, no debe indexarse.
      const meta = document.createElement('meta')
      meta.name = 'robots'
      meta.content = 'noindex, nofollow'
      document.head.appendChild(meta)
    })

    return {
      t,
      state,
      page,
      step,
      pickedPlayer,
      typedName,
      canContinue,
      goToForm,
      form,
      formRef,
      rules,
      phoneCodeOptions,
      doc,
      docLang,
      paragraphs,
      reservationLabel,
      publishedLabel,
      pad,
      hasStroke,
      padStart,
      padMove,
      padEnd,
      padClear,
      pickPlayer,
      initials,
      submit,
      submitting,
      errorMsg,
      downloadApp,
      icons: { mdiAccount, mdiCheck, mdiCheckCircle, mdiQrcodeRemove },
    }
  },
}
</script>

<style lang="scss" scoped>
// Esquema Fanaty (manager/docs/fanaty-context.md): primario #7C3AED →
// #9F3DFF, fondo oscuro #121212, superficie #2B2B2B, éxito #14AE5C, error
// #FF3232, Poppins (500 cuerpo / 800 títulos). Igual que el mockup de la
// sección 2 del ticket 10: página oscura, tarjetas, CTA con gradiente.
$primary: #7c3aed;
$primary-light: #9f3dff;
$bg: #121212;
$surface: #1e1e1e;
$surface-2: #2b2b2b;
$border: #3a3a3a;
$text: #ffffff;
$muted: #a0a0a0;
$success: #14ae5c;
$error: #ff3232;

.waiver-page {
  min-height: 100vh;
  background: $bg;
  color: $text;
  padding: 16px;
  max-width: 640px;
  margin: 0 auto;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
}
.waiver-card {
  background: $surface;
  border: 1px solid $border;
  border-radius: 20px;
  padding: 20px;
}
.waiver-center { text-align: center; padding: 40px 20px; }
.waiver-eyebrow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: $muted;
  margin: 4px 0 12px;
}
.waiver-eyebrow-text { font-size: 11px; font-weight: 600; letter-spacing: 0.6px; text-transform: uppercase; color: $muted; }
.waiver-logo { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; background: $surface-2; }
.waiver-h1 { font-size: 22px; font-weight: 800; line-height: 1.2; margin: 0 0 4px; color: $text; }
.waiver-sub { color: $muted; font-size: 14px; margin: 0 0 12px; }

// ── Roster (paso 1)
.waiver-roster { display: flex; flex-direction: column; gap: 8px; }
.waiver-player {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: $surface-2;
  border: 1.5px solid $border;
  border-radius: 14px;
  padding: 12px 14px;
  color: $text;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: lighten($border, 12%); }
  &:focus-visible { outline: 2px solid $primary-light; outline-offset: 2px; }
  &.is-on { border-color: $primary; background: rgba($primary, 0.12); }
  &.is-signed { opacity: 0.55; cursor: default; }
}
.waiver-avatar {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba($primary, 0.2);
  color: $primary-light;
  font-size: 12px;
  font-weight: 700;
}
.waiver-avatar-icon { color: $primary-light !important; }
.waiver-player-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.waiver-player-meta { color: $muted; font-size: 12px; font-weight: 500; }
.waiver-radio {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid $border;
  position: relative;
  .is-on & { border-color: $primary; }
  .is-on &::after {
    content: '';
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    background: $primary;
  }
}
.waiver-check {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba($success, 0.18);
  .v-icon { color: $success !important; }
}
.waiver-divider {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: $muted;
  text-align: center;
  margin: 18px 0 8px;
}

// ── Formulario (paso 2)
.waiver-back { background: none; border: 0; color: $muted; font: inherit; font-size: 14px; padding: 0 0 8px; cursor: pointer; }
.waiver-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: $muted;
  margin: 8px 0 6px;
}
.waiver-code { max-width: 140px; }
// Inputs de Vuetify sobre la superficie oscura: fondo, borde y radio del
// mockup. `dark` en el componente ya pone el texto/placeholder claros.
.waiver-page ::v-deep {
  .v-input--is-focused .v-input__slot fieldset { border-color: $primary !important; }
  .v-text-field--outlined .v-input__slot { background: $surface-2; border-radius: 12px; min-height: 48px; }
  .v-text-field--outlined fieldset { border-color: $border; }
  .v-text-field--outlined.v-input--has-state fieldset { border-color: $error; }
  .v-input input, .v-select__selection { color: $text; }
  .v-messages__message { color: $error; }
  .v-input--checkbox .v-label { color: $text; font-size: 13px; line-height: 1.35; }
  .v-input--checkbox .v-icon { color: $muted; }
  .v-input--checkbox.v-input--is-label-active .v-icon { color: $primary !important; }
}
.waiver-consent-box {
  background: $surface-2;
  border: 1px solid $border;
  border-radius: 12px;
  padding: 10px 12px;
  margin: 4px 0 8px;
}
.waiver-doc-head { display: flex; align-items: center; justify-content: space-between; }
.waiver-lang button {
  background: none;
  border: 1px solid $border;
  border-radius: 999px;
  padding: 2px 10px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  margin-left: 4px;
  cursor: pointer;
  color: $muted;
  &.on { background: $primary; border-color: $primary; color: #fff; }
}
.waiver-doc-title { font-size: 16px; font-weight: 800; margin: 8px 0; color: $text; }
.waiver-doc {
  max-height: 320px;
  overflow-y: auto;
  background: $surface-2;
  border: 1px solid $border;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.45;
  color: #e6e6e6;
  p { margin: 0 0 4px; }
  .waiver-conspicuous { font-weight: 800; color: $text; }
}

// ── Firma: el pad queda CLARO a propósito. El PNG con el trazo #111 va tal
// cual al PDF (papel blanco); un trazo claro sobre fondo oscuro se perdería.
.waiver-pad { position: relative; }
.waiver-canvas {
  width: 100%;
  height: 160px;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 12px;
  touch-action: none;
  display: block;
}
.waiver-pad::after {
  // línea base punteada, como el mockup
  content: '';
  position: absolute;
  left: 20px;
  right: 20px;
  bottom: 34px;
  border-bottom: 1.5px dashed #c9c9c9;
  pointer-events: none;
}
.waiver-clear {
  position: absolute;
  right: 12px;
  top: 10px;
  background: none;
  border: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
}
.waiver-econsent { font-size: 11px; color: $muted; margin: 10px 0; }
.waiver-error { color: $error; font-size: 13px; margin: 6px 0; }

// ── CTA con el gradiente de marca (Continue / Sign — I agree / Get the app)
.waiver-page ::v-deep .waiver-cta.v-btn {
  background: linear-gradient(90deg, $primary 0%, $primary-light 100%) !important;
  color: #fff !important;
  border-radius: 14px;
  height: 52px;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 15px;
  letter-spacing: 0;
  text-transform: none;
  box-shadow: 0 8px 24px rgba($primary, 0.35);
  &.v-btn--disabled { opacity: 0.4; color: #fff !important; box-shadow: none; }
}

// ── Tablet del club (mostrador): documento a la izquierda, datos + firma a
// la derecha, todo en una pantalla; pad más alto para firmar con el dedo.
.waiver-col--doc { margin-top: 24px; }
.waiver-col--sign { margin-top: 24px; }
@media (min-width: 900px) {
  .waiver-page { max-width: 1100px; padding: 24px; }
  .waiver-grid {
    display: grid;
    grid-template-columns: 1.15fr 1fr;
    grid-template-rows: auto 1fr;
    column-gap: 28px;
  }
  .waiver-col--doc { grid-column: 1; grid-row: 1 / span 2; margin-top: 0; }
  .waiver-col--form { grid-column: 2; grid-row: 1; }
  .waiver-col--sign { grid-column: 2; grid-row: 2; margin-top: 8px; align-self: end; }
  .waiver-doc { max-height: none; }
  .waiver-canvas { height: 240px; }
  .waiver-h1 { font-size: 26px; }
}
</style>
